import { Fragment, useMemo, useState } from 'react';
import type { BranchBreakdownPoint } from '@ace-tracker/shared';
import { DeveloperName } from '../settings';
import { ExportMenu } from './ExportMenu';
import { InfoTooltip } from './InfoTooltip';

/**
 * Repository-grouped view of the developer x repository x git branch x model x
 * day usage for ONE tool — every row comes from that tool's own query catalog,
 * never merged across tools. Each repository gets a summary row (totals across
 * its branches) that expands to the per-branch detail rows beneath it.
 *
 * Branch names only exist with the collector's `sanitization.sendGitBranch`
 * opt-in, repository names with `sanitization.sendGitRepository` (see
 * collector/docs/PRIVACY.md). Rows without a repository are gathered in one
 * "Repository not collected" group instead of being dropped.
 */

const NO_REPO = '';

interface RepoGroup {
  repository: string;
  rows: BranchBreakdownPoint[];
  branches: number;
  developers: number;
  requests: number;
  input: number;
  output: number;
}

function groupByRepository(rows: BranchBreakdownPoint[]): RepoGroup[] {
  const map = new Map<string, BranchBreakdownPoint[]>();
  for (const row of rows) {
    const key = row.repository || NO_REPO;
    const list = map.get(key);
    if (list) list.push(row);
    else map.set(key, [row]);
  }
  const groups: RepoGroup[] = [];
  for (const [repository, list] of map) {
    // Branch A-Z, then newest day first, so each branch's rows sit together.
    const sorted = [...list].sort(
      (a, b) => a.branch.localeCompare(b.branch) || b.date.localeCompare(a.date) || a.model.localeCompare(b.model),
    );
    groups.push({
      repository,
      rows: sorted,
      branches: new Set(sorted.map((r) => r.branch)).size,
      developers: new Set(sorted.map((r) => r.developerId)).size,
      requests: sorted.reduce((s, r) => s + r.events, 0),
      input: sorted.reduce((s, r) => s + r.inputTokens, 0),
      output: sorted.reduce((s, r) => s + r.outputTokens, 0),
    });
  }
  // Busiest repository first; the "not collected" bucket always last.
  return groups.sort((a, b) => {
    if (!a.repository !== !b.repository) return a.repository ? -1 : 1;
    return b.requests - a.requests || b.input + b.output - (a.input + a.output) || a.repository.localeCompare(b.repository);
  });
}

export function RepositoryBreakdownTable({ byBranch, info }: { byBranch: BranchBreakdownPoint[]; info?: string }) {
  const groups = useMemo(() => groupByRepository(byBranch), [byBranch]);
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div data-export-root className="rounded-card border border-border bg-surface p-5 shadow-sm">
      <ExportMenu title="Measurements by repository" className="float-right -mr-2 -mt-2 ml-2" />
      <h3 className="flex items-center gap-1.5 text-sm font-medium text-text-default">
        Measurements by repository
        {info && <InfoTooltip text={info} />}
      </h3>
      <p className="mt-1 text-xs text-text-muted">Repository → branch. Click a row to expand.</p>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[960px] text-left text-sm">
          <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
            <tr>
              <th className="pb-2.5 pr-3">Repository / Branch</th>
              <th className="pb-2.5 pr-3">Developer</th>
              <th className="pb-2.5 pr-3">Model</th>
              <th className="pb-2.5 pr-3 text-right">Requests</th>
              <th className="pb-2.5 pr-3 text-right">In</th>
              <th className="pb-2.5 pr-3 text-right">Out</th>
              <th className="pb-2.5 pr-3 text-right">Total</th>
              <th className="pb-2.5">Date</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const key = g.repository || '__none__';
              const isOpen = expanded === key;
              const toggle = () => setExpanded(isOpen ? null : key);
              return (
                <Fragment key={key}>
                  <tr
                    tabIndex={0}
                    role="button"
                    aria-expanded={isOpen}
                    onClick={toggle}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle();
                      }
                    }}
                    className="cursor-pointer border-b border-border hover:bg-surface-alt focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-sky-blue"
                  >
                    <td className="py-2.5 pr-3 font-medium text-text-default">
                      <span className="inline-flex items-center gap-2">
                        <span
                          className={`inline-block h-0 w-0 border-y-4 border-y-transparent border-l-[5px] border-l-text-faint transition-transform ${isOpen ? 'rotate-90' : ''}`}
                          aria-hidden="true"
                        />
                        {g.repository ? (
                          <span className="break-all font-mono text-xs">{g.repository}</span>
                        ) : (
                          <span
                            className="italic text-text-muted"
                            title="Repository not collected (sanitization.sendGitRepository is off)"
                          >
                            Repository not collected
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-text-muted">{g.developers.toLocaleString()} {g.developers === 1 ? 'developer' : 'developers'}</td>
                    <td className="py-2.5 pr-3 text-text-muted">{g.branches.toLocaleString()} {g.branches === 1 ? 'branch' : 'branches'}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{g.requests.toLocaleString()}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{g.input.toLocaleString()}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{g.output.toLocaleString()}</td>
                    <td className="py-2.5 pr-3 text-right font-semibold tabular-nums text-text-default">
                      {(g.input + g.output).toLocaleString()}
                    </td>
                    <td className="py-2.5">
                      <span className="rounded-full bg-primary-blue/10 px-2.5 py-0.5 text-xs font-medium text-primary-blue">
                        {new Set(g.rows.map((r) => r.date)).size} {new Set(g.rows.map((r) => r.date)).size === 1 ? 'day' : 'days'}
                      </span>
                    </td>
                  </tr>
                  {isOpen &&
                    g.rows.map((row) => (
                      <tr
                        key={`${row.developerId}:${row.branch}:${row.model}:${row.date}`}
                        className="border-b border-border bg-surface-alt text-xs"
                      >
                        <td className="max-w-[320px] break-all py-2.5 pl-7 pr-3 font-mono text-text-default">{row.branch}</td>
                        <td className="py-2.5 pr-3 text-text-default">
                          <DeveloperName developerId={row.developerId} />
                        </td>
                        <td className="py-2.5 pr-3 font-medium text-text-default">{row.model}</td>
                        <td className="py-2.5 pr-3 text-right tabular-nums text-text-muted">{row.events.toLocaleString()}</td>
                        <td className="py-2.5 pr-3 text-right tabular-nums text-text-muted">{row.inputTokens.toLocaleString()}</td>
                        <td className="py-2.5 pr-3 text-right tabular-nums text-text-muted">{row.outputTokens.toLocaleString()}</td>
                        <td className="py-2.5 pr-3 text-right font-medium tabular-nums text-text-default">
                          {(row.inputTokens + row.outputTokens).toLocaleString()}
                        </td>
                        <td className="py-2.5 tabular-nums text-text-muted">{row.date}</td>
                      </tr>
                    ))}
                </Fragment>
              );
            })}
            {groups.length === 0 && (
              <tr>
                <td className="py-3 text-text-muted" colSpan={8}>
                  No branch data in this range. Branch names are only collected when the collector config sets{' '}
                  <code className="font-mono text-xs">sanitization.sendGitBranch</code> to true.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
